import { Injectable } from '@angular/core';
import { Dropbox, DropboxAuth, users } from 'dropbox';
import { environment } from '../../../../environments/environment';
import { LocalStorage } from '../../auth/local.storage';
import { isK3000domain } from '../../../core/common';
import { CloudAuthBaseService, AuthModel, CLOUD_PKCE_VERIFIER_KEY } from '../cloud-auth-base.service';

const DROPBOX_INFO_KEY = 'DROPBOX_INFO_KEY';

@Injectable({
  providedIn: 'root'
})

export class DropboxAuthService extends CloudAuthBaseService {
  private get clientId(): string {
    return isK3000domain() ? environment.kurzweil.dropbox.AppKey : environment.alinea.dropbox.AppKey;
  }

  private get redirectUri(): string {
    return `${window.location.origin}/cloud-redirect/dropbox`;
  }

  constructor(protected readonly localStorage: LocalStorage) {
    super(localStorage);
  }

  async authenticateDropbox(): Promise<void> {
    const dbxAuth = new DropboxAuth({ clientId: this.clientId });

    // getAuthenticationUrl with authType='code' and usePKCE=true generates the
    // PKCE verifier/challenge internally and appends code_challenge to the URL.
    const authUrl = await dbxAuth.getAuthenticationUrl(
      this.redirectUri, undefined, 'code', 'online', undefined, 'none', true
    ) as string;

    // Persist the SDK-generated verifier so DbRedirectComponent can use it for token exchange.
    this.localStorage.set(CLOUD_PKCE_VERIFIER_KEY, dbxAuth.getCodeVerifier());

    const options: AuthModel = {
      localStorageKey: DROPBOX_INFO_KEY,
      url: authUrl,
      windowName: 'DropboxAuthPopup'
    };

    await this.authorizePopup(options);
  }

  async getUserInfo(): Promise<users.FullAccount | null> {
    const authData: DropboxAuthInfo | null = this.localStorage.getWithExpiry(DROPBOX_INFO_KEY);
    if (authData) {
      const dbx = new Dropbox({ accessToken: authData.access_token });
      const response = await dbx.usersGetCurrentAccount();
      return (response as any).result ?? response;
    }
    return null;
  }
}

export interface DropboxAuthInfo {
  access_token: string;
  refresh_token?: string;
  account_id: string;
  token_type: string;
  uid: string;
  expires_in: number;
}
